import React, { createContext, useState, useContext, useEffect, useCallback } from 'react';
import { getApiUrl } from '../config/apiConfig';
import { useAuth } from './AuthContext';

const CajaContext = createContext();

export const useCaja = () => useContext(CajaContext);

export const CajaProvider = ({ children }) => {
  const [cajasAbiertas, setCajasAbiertas] = useState([]);
  const [cajaActiva, setCajaActiva] = useState(null); // { id, nombre, fecha_apertura }
  const [loading, setLoading] = useState(true);
  const { auth } = useAuth();

  // Recarga el estado de todas las cajas abiertas desde el backend
  const recargarEstadoCaja = useCallback(async () => {
    if (!auth?.token) {
      setCajasAbiertas([]);
      setCajaActiva(null);
      setLoading(false);
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(getApiUrl('/caja/estado'), {
        headers: { 'Authorization': `Bearer ${auth.token}` }
      });

      if (response.ok) {
        const data = await response.json();
        const nuevasCajas = data.cajasAbiertas || [];
        const cajaAsignadaId = data.cajaAsignada || null;
        setCajasAbiertas(nuevasCajas);

        setCajaActiva(prev => {
          if (nuevasCajas.length === 0) return null;

          // Si el usuario es CAJERO: solo puede ver su caja asignada específica
          if (auth?.user?.rol === 'CAJERO') {
            if (cajaAsignadaId) {
              const cajaDelCajero = nuevasCajas.find(c => c.id === cajaAsignadaId);
              // Si la caja asignada existe y está abierta → seleccionarla
              if (cajaDelCajero) return cajaDelCajero;
            }
            // Sin caja asignada válida (no existe o fue cerrada) → no mostrar ninguna caja
            return null;
          }

          // Para ENCARGADO y otros roles:
          // Si la caja activa anterior sigue abierta, mantenerla
          if (prev && nuevasCajas.some(c => c.id === prev.id)) {
            return nuevasCajas.find(c => c.id === prev.id);
          }

          // Por defecto, primera caja disponible
          return nuevasCajas[0];
        });
      } else {
        setCajasAbiertas([]);
        setCajaActiva(null);
      }
    } catch (error) {
      console.error("Error verificando estado de caja:", error);
      setCajasAbiertas([]);
      setCajaActiva(null);
    } finally {
      setLoading(false);
    }
  }, [auth?.token]);

  // Efecto inicial: verificar al montar o cuando cambia el usuario
  useEffect(() => {
    recargarEstadoCaja();
  }, [auth, recargarEstadoCaja]);

  // Llamar tras apertura exitosa: agrega la nueva caja y la establece como activa
  const abrirCaja = (nuevaCaja) => {
    // nuevaCaja puede ser { id, nombre, fecha_apertura } o solo fecha_apertura (compatibilidad)
    if (typeof nuevaCaja === 'string') {
      // Compatibilidad: si se pasa solo fecha_apertura como string, recargar
      recargarEstadoCaja();
      return;
    }
    setCajasAbiertas(prev => {
      const existe = prev.some(c => c.id === nuevaCaja.id);
      return existe ? prev : [...prev, nuevaCaja];
    });
    setCajaActiva(nuevaCaja);
  };

  // Llamar tras cierre exitoso: elimina la caja cerrada de la lista
  const cerrarCaja = (idCajaCerrada) => {
    if (idCajaCerrada) {
      setCajasAbiertas(prev => {
        const restantes = prev.filter(c => c.id !== idCajaCerrada);
        // Si la caja activa era la que se cerró, cambiar a la primera disponible
        setCajaActiva(prevActiva => {
          if (prevActiva?.id === idCajaCerrada) {
            return restantes.length > 0 ? restantes[0] : null;
          }
          return prevActiva;
        });
        return restantes;
      });
    } else {
      // Sin id específico: recargar todo
      recargarEstadoCaja();
    }
  };

  // Cambiar la caja activa (para ENCARGADO con múltiples cajas)
  const seleccionarCaja = (caja) => {
    setCajaActiva(caja);
  };

  // Objeto `caja` con compatibilidad hacia atrás (para componentes que usan caja.estaAbierta)
  const caja = {
    estaAbierta: cajasAbiertas.length > 0,
    fechaApertura: cajaActiva?.fecha_apertura || null,
    loading: loading,
    id: cajaActiva?.id || null,
    nombre: cajaActiva?.nombre || null,
  };

  const valor = {
    caja,
    cajasAbiertas,
    cajaActiva,
    abrirCaja,
    cerrarCaja,
    seleccionarCaja,
    recargarEstadoCaja,
  };

  return (
    <CajaContext.Provider value={valor}>
      {children}
    </CajaContext.Provider>
  );
};
